import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, RadioGroup, FormControlLabel, Radio, Checkbox, Divider, useMediaQuery, Chip, Paper } from '@mui/material';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import TreeDropdown, { TreeDropdownOption } from '../tree-dropdown';
import OneTimeProvision from './parts/one-time-provision';
import RecurringProvision from './parts/recurring-provision';
import AddBankAccount from './add-bank';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useUser } from '../../providers/user-provider';
import { Provision } from '../../pages/provisions';
import { useToast } from '../../providers/toaster-provider';
import DeletePopup from './delete';
import DeletePopupMobile from './mobile/delete-mobile';
import { SlideUpTransition } from './mobile/mobile-bottom-sheet';
import AutoProvisionMetaModal from './auto-provision-meta-modal';
import theme from '../../util/theme/theme';
import provisionImage from '../../files/images/transaction.png';
import { useNavigate } from 'react-router-dom';
import { OCCURRENCES_PER_YEAR } from '../../util/recurrence';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { Users, Eye } from 'lucide-react';
import { useSettings } from '../../providers/settings-provider';
import ProvisionOccurrencesPanel from './parts/provision-occurrences-panel';

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ordinalSuffix = (n: number): string => {
    if (n % 100 >= 11 && n % 100 <= 13) return 'th';
    return ['th', 'st', 'nd', 'rd'][n % 10] || 'th';
};

// services/auto-provision-service.js stores a bare interval word (e.g. "monthly")
// as recurrencePattern for pattern-detected templates, instead of the detailed
// "monthly-15th" style string the Schedule component (the "Every ___" picker)
// expects — so it can't parse day/date/quarter/month out of it and the field
// renders blank. Derive the missing detail from the template's own startDate so
// the Edit modal shows a real, editable value instead of nothing.
const normalizeRecurrencePattern = (pattern: string, referenceDate?: string | null): string => {
    if (!pattern || pattern.includes('-')) return pattern;
    if (!referenceDate) return pattern;
    const d = new Date(referenceDate);
    if (isNaN(d.getTime())) return pattern;
    const day = d.getDate();
    const suffix = ordinalSuffix(day);
    switch (pattern) {
        case 'weekly':
            return `weekly-${WEEKDAY_NAMES[d.getDay()]}`;
        case 'fortnightly':
            return `fortnightly-${WEEKDAY_NAMES[d.getDay()]}`;
        case 'monthly':
            return `monthly-${day}${suffix}`;
        case 'quarterly':
            return `quarterly-Q${Math.floor(d.getMonth() / 3) + 1}-${day}${suffix}`;
        case 'half-yearly':
            return `half-yearly-${d.getMonth() < 6 ? 'H1' : 'H2'}-${day}${suffix}`;
        case 'yearly':
            return `yearly-${MONTH_NAMES[d.getMonth()]}-${day}${suffix}`;
        default:
            return pattern;
    }
};

interface AddProvisionPopupProps {
    open: boolean;
    onClose: () => void;
    onCreate: (data: Provision) => void;
    onDelete?: (id: number) => void;
    isEdit?: { id: number | null, state: boolean };
    initialStartDate?: string;
    initialAmount?: string;
    initialDescription?: string;
    intervalAnalysis?: { interval: string; simplifiedDescription: string; startDate: string; amount: number; message: string; suggestedCategoryId?: number };
    sourceTransactionGuid?: string;
    isLoading?: boolean;
    // Merge mode: combining several existing templates into this one new
    // template. When set, the modal shows a "From these" table of the
    // sources above the normal fields, prefills from mergeDefaults, and
    // recalculates the amount field from mergeYearlyTotal whenever the
    // recurrence period changes (unless the user typed into amount
    // themselves, in which case changing period warns first).
    mergeSources?: Provision[];
    mergeYearlyTotal?: number;
    mergeDefaults?: { startDate: string; endDate: string; endDateType: 'on' | 'after' | 'never'; categoryId?: number; bankAccountId?: number; appliedTaxBreakdown?: { id: number; name: string }[]; description: string };
    mergeCategories?: DropdownOption[];
}

const AddProvisionPopup: React.FC<AddProvisionPopupProps> = ({ open, onClose, onCreate, onDelete, isEdit, initialStartDate, initialAmount, initialDescription, intervalAnalysis, sourceTransactionGuid, isLoading = false, mergeSources, mergeYearlyTotal, mergeDefaults, mergeCategories }) => {
    const { user } = useUser();
    const { showError, showSuccess } = useToast();
    const { formatDate, formatAmount } = useSettings();
    const navigate = useNavigate();
    // New provisions can't be backdated - see validate() below, which is the
    // real enforcement (this just keeps the calendar from offering a date
    // it would reject anyway). Not applied to edits of existing provisions,
    // whose date may legitimately already be in the past.
    const todayStr = new Date().toISOString().slice(0, 10);
    const [amount, setAmount] = useState('');
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // Same prop contract on both - picks the bottom-sheet confirm dialog on
    // mobile instead of duplicating this form's ~10 DeletePopup call sites.
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const [description, setDescription] = useState('');
    const [type, setType] = useState<'one-time' | 'recurring'>('recurring');
    const [date, setDate] = useState('');
    const [category, setCategory] = useState('');
    const [bank, setBank] = useState('');
    const [recurrenceType, setRecurrenceType] = useState<'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom'>('monthly');
    const [recurrencePattern, setRecurrencePattern] = useState<string>('');
    const [occurrences, setOccurrences] = useState(1);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [endDateType, setEndDateType] = useState<'on' | 'after' | 'never'>('never');
    const [appliedTaxes, setAppliedTaxes] = useState<{ id: number | undefined, name: string }[]>([]);
    const [taxManuallyEdited, setTaxManuallyEdited] = useState(false);
    const [pendingTaxToggle, setPendingTaxToggle] = useState<{ id: number | undefined, name: string }[] | null>(null);
    const [taxDefinitions, setTaxDefinitions] = useState<{ id: number, name: string }[]>([]);
    const [categoryDefaultTaxIds, setCategoryDefaultTaxIds] = useState<Record<string, number[]>>({});
    const [categoryOptions, setCategoryOptions] = useState<TreeDropdownOption[]>([]);
    const [bankOptions, setBankOptions] = useState<DropdownOption[]>([]);
    const [openModal, setOpenModal] = useState<'cancel' | 'linkedTransactionsWarning' | 'addBank' | 'delete' | 'autoProvisionMeta' | 'recalculateAmountWarning' | 'occurrenceHistoryChoice' | ''>('');
    const [hasLinkedTransactions, setHasLinkedTransactions] = useState(false);
    const [hasShownLinkedTransactionsWarning, setHasShownLinkedTransactionsWarning] = useState(false);
    const [pendingFieldChange, setPendingFieldChange] = useState<{ field: string, value: any } | null>(null);
    // Merge mode's own dirty-flag, mirroring taxManuallyEdited - once the user
    // types into amount themselves, changing the recurrence period no longer
    // silently overwrites it (see setRecurrenceType wiring on RecurringProvision).
    const [amountManuallyEdited, setAmountManuallyEdited] = useState(false);
    const [pendingRecurrenceType, setPendingRecurrenceType] = useState<'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom' | null>(null);
    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [autoProvisionMeta, setAutoProvisionMeta] = useState<any>(null);
    // Only populated (and shown) when editing a recurring template that
    // actually has soft-deleted or manually-pushed unlinked occurrences -
    // lets the user choose whether this edit should regenerate those slots
    // fresh or leave that history alone (the default). See
    // updateRecurringProvisions, provision-service.js.
    const [occurrenceHistory, setOccurrenceHistory] = useState<{ hasDeleted: boolean, hasPushed: boolean, deletedCount: number, pushedCount: number } | null>(null);
    const [regenerateAll, setRegenerateAll] = useState(false);
    // "Occurrences" panel for recurring templates - closed by default each
    // time an edit session starts, openable via the toggle button next to
    // the modal title and closable via the panel's own X.
    const [showOccurrencesPanel, setShowOccurrencesPanel] = useState(false);
    const [errors, setErrors] = useState<{ 
        amount?: string, 
        description?: string, 
        type?: string, 
        date?: string, 
        category?: string,
        recurrenceType?: string,
        occurrences?: string,
        recurrencePattern?: string,
        startDate?: string,
        endDate?: string,
        appliedTaxes?: string
    }>({});
    const [initialState, setInitialState] = useState({
        amount: initialAmount || '',
        description: initialDescription || '',
        type: 'recurring' as 'one-time' | 'recurring',
        date: '',
        category: '',
        bank: '',
        recurrenceType: 'monthly' as 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom',
        recurrencePattern: '',
        occurrences: 1,
        startDate: initialStartDate || '',
        endDate: '',
        endDateType: 'never' as 'on' | 'after' | 'never',
        appliedTaxes: [] as { id: number | undefined, name: string }[]
    });

    const [fetchCategories, setFetchCategories] = useApi();
    const [fetchBanks, setFetchBanks] = useApi();
    const [createBanks, setCreateBanks] = useApi();
    const [fetchProvision, setFetchProvision] = useApi();
    const [deleteProvision, setDeleteProvision] = useApi();
    const [fetchTaxDefinitions, setFetchTaxDefinitions] = useApi();
    const [fetchOccurrenceHistory, setFetchOccurrenceHistory] = useApi();
    const [fetchOccurrenceTransactions, setFetchOccurrenceTransactions] = useApi();

    React.useEffect(() => {
        if (!open) return;
        const { url, method } = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetchCategories({ url, method });
    }, [open, user?.entityId, setFetchCategories]);

    React.useEffect(() => {
        if (!open || !user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.taxDefinitions.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchTaxDefinitions({ url, method });
    }, [open, user?.entityId, setFetchTaxDefinitions]);

    React.useEffect(() => {
        const { data, isLoading } = fetchTaxDefinitions;
        if (!isLoading && data) {
            const list = Array.isArray(data) ? data : data.taxDefinitions ?? [];
            setTaxDefinitions(list.filter((t: any) => t.isActive && t.isEnabled));
        }
    }, [fetchTaxDefinitions]);

    React.useEffect(() => {
        if (!open) return;
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setFetchBanks({ url, method });
    }, [open, user?.entityId, setFetchBanks, createBanks]);

    React.useEffect(() => {
        if (!isEdit?.state) return;
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.fetchById, [{ param: 'id', value: isEdit?.id }]);
        setFetchProvision({ url, method });
    }, [isEdit?.state, isEdit?.id, user?.entityId, setFetchProvision]);

    React.useEffect(() => {
        if (!isEdit?.state || !isEdit?.id) return;
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.occurrenceHistory, [{ param: 'id', value: isEdit.id }]);
        setFetchOccurrenceHistory({ url, method });
    }, [isEdit?.state, isEdit?.id, setFetchOccurrenceHistory]);

    React.useEffect(() => {
        if (fetchOccurrenceHistory.data) {
            setOccurrenceHistory(fetchOccurrenceHistory.data);
        }
    }, [fetchOccurrenceHistory.data]);

    React.useEffect(() => {
        if (!isEdit?.state || !isEdit?.id) return;
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.fetchTransactionsById, [{ param: 'id', value: isEdit.id }]);
        setFetchOccurrenceTransactions({ url, method });
    }, [isEdit?.state, isEdit?.id, setFetchOccurrenceTransactions]);

    // Reset to closed every time a fresh edit session starts, rather than
    // staying open from whatever the user left it at on a previous provision.
    React.useEffect(() => {
        if (open && isEdit?.state) setShowOccurrencesPanel(false);
    }, [open, isEdit?.state, isEdit?.id]);


    React.useEffect(() => {
        if (open && !isEdit?.state && mergeSources?.length) {
            // Merge mode: prefill from the computed defaults instead of a
            // blank reset. recurrenceType always starts at 'monthly' (the
            // amount was already computed against a monthly period), the
            // user can change it afterwards which recalculates amount (see
            // the RecurringProvision setRecurrenceType wiring below).
            setAmount(mergeYearlyTotal != null ? String(Math.round((mergeYearlyTotal / OCCURRENCES_PER_YEAR.monthly) * 100) / 100) : '');
            setAmountManuallyEdited(false);
            setDescription(mergeDefaults?.description || '');
            setType('recurring');
            // Sources agreed on tax -> prefill it and mark it manually-edited
            // so the category-driven auto-populate effect below doesn't
            // immediately overwrite it with that category's generic
            // defaults. Sources disagreed -> leave it blank and let that
            // effect apply normally once the user actually picks a category.
            setAppliedTaxes(mergeDefaults?.appliedTaxBreakdown || []);
            setTaxManuallyEdited(!!mergeDefaults?.appliedTaxBreakdown);
            setBank(mergeDefaults?.bankAccountId ? String(mergeDefaults.bankAccountId) : '');
            setRecurrenceType('monthly');
            setRecurrencePattern('');
            setOccurrences(1);
            setStartDate(mergeDefaults?.startDate || '');
            setEndDate(mergeDefaults?.endDate || '');
            setEndDateType(mergeDefaults?.endDateType || 'never');
            setCategory(mergeDefaults?.categoryId ? String(mergeDefaults.categoryId) : '');
            setErrors({});
        }
    }, [open, isEdit?.state, mergeSources, mergeYearlyTotal, mergeDefaults]);

    React.useEffect(() => {
        if (open && !isEdit?.state && !mergeSources?.length) {
            // Reset state for new provision creation, using initialState values
            setAmount(initialAmount || '');
            setDescription(initialDescription || '');
            setType('recurring');
            setDate('');
            setAppliedTaxes([]);
            setTaxManuallyEdited(false);
            setBank('');
            setRecurrenceType('monthly');
            setRecurrencePattern('');
            setOccurrences(1);
            setStartDate(initialStartDate || '');
            setEndDate('');
            setEndDateType('never');
            setErrors({});
            
            // Apply interval analysis if available
            if (intervalAnalysis?.interval && intervalAnalysis.interval !== 'unknown') {
                const patternMap: { [key: string]: 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | 'custom' } = {
                    'daily': 'daily',
                    'weekly': 'weekly',
                    'fortnightly': 'fortnightly',
                    'bi-weekly': 'fortnightly',
                    'monthly': 'monthly',
                    'quarterly': 'quarterly',
                    'half-yearly': 'half-yearly',
                    'bi-annual': 'half-yearly',
                    'yearly': 'yearly',
                    'annual': 'yearly'
                };
                const detectedType = patternMap[intervalAnalysis.interval.toLowerCase()];
                if (detectedType) {
                    setRecurrenceType(detectedType);
                }
            }
            
            // Apply suggested category if available (do this after other resets)
            if (intervalAnalysis?.suggestedCategoryId) {
                setCategory(String(intervalAnalysis.suggestedCategoryId));
            } else {
                setCategory('');
            }
        }
    }, [open, isEdit?.state, initialAmount, initialDescription, initialStartDate, intervalAnalysis]);

    React.useEffect(() => {
        // When type changes between one-time and recurring, sync the date fields
        if (type === 'one-time' && startDate && !date) {
            setDate(startDate);
        } else if (type === 'recurring' && date && !startDate) {
            setStartDate(date);
        }
    }, [type, date, startDate]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchCategories;
        if (data && !isLoading) {
            // Transform hierarchical data to TreeDropdownOption format
            const transformCategory = (cat: any): TreeDropdownOption => ({
                id: cat.id.toString(),
                label: cat.name,
                children: cat.children ? cat.children.map(transformCategory) : undefined
            });
            
            const hierarchicalOptions: TreeDropdownOption[] = data.map(transformCategory);
            setCategoryOptions(hierarchicalOptions);

            // Flatten each category's default tax IDs for the auto-populate logic below
            const taxMap: Record<string, number[]> = {};
            const walk = (cats: any[]) => {
                cats.forEach((cat: any) => {
                    taxMap[String(cat.id)] = cat.defaultTaxIds || [];
                    if (cat.children?.length) walk(cat.children);
                });
            };
            walk(data);
            setCategoryDefaultTaxIds(taxMap);
        }
        if (error) {
            console.error('Error fetching categories:', error);
        }
    }, [fetchCategories]);

    // Auto-populate applied taxes from the selected category's defaults, but only
    // until the user manually touches the tax checkboxes themselves
    React.useEffect(() => {
        if (!category || taxManuallyEdited) return;
        const defaultIds = categoryDefaultTaxIds[category] ?? [];
        setAppliedTaxes(prev => {
            const prevIds = [...prev.map(t => t.id)].sort();
            const nextIds = [...defaultIds].sort();
            const unchanged = prevIds.length === nextIds.length && prevIds.every((id, i) => id === nextIds[i]);
            // Bail out (return the same reference) when nothing actually changed, so this
            // effect can't keep re-triggering itself via a fresh array on every run
            if (unchanged) return prev;
            return defaultIds.map(id => ({ id, name: taxDefinitions.find(t => t.id === id)?.name || '' }));
        });
    }, [category, taxManuallyEdited, categoryDefaultTaxIds, taxDefinitions]);

    const toggleTax = (tax: { id: number, name: string }) => {
        const next = appliedTaxes.some(t => t.id === tax.id)
            ? appliedTaxes.filter(t => t.id !== tax.id)
            : [...appliedTaxes, tax];

        const defaultIds = categoryDefaultTaxIds[category] ?? [];
        const matchesDefault = defaultIds.length === next.length && defaultIds.every(id => next.some(t => t.id === id));

        if (matchesDefault) {
            setTaxManuallyEdited(true);
            showLinkedTransactionsWarning('appliedTaxes', next);
        } else {
            setPendingTaxToggle(next);
        }
    };

    const confirmTaxToggle = () => {
        if (pendingTaxToggle) {
            setTaxManuallyEdited(true);
            showLinkedTransactionsWarning('appliedTaxes', pendingTaxToggle);
        }
        setPendingTaxToggle(null);
    };

    // Merge mode only: applies a recurrence-period change and, unless the
    // user has manually typed into amount, recalculates it from the sources'
    // combined yearly total. 'custom' has no occurrences-per-year to divide
    // by, so it's left untouched either way.
    const applyRecurrenceTypeChange = (val: typeof recurrenceType) => {
        setRecurrenceType(val);
        setRecurrencePattern('');
        if (mergeYearlyTotal != null && val !== 'custom' && OCCURRENCES_PER_YEAR[val]) {
            setAmount(String(Math.round((mergeYearlyTotal / OCCURRENCES_PER_YEAR[val]) * 100) / 100));
            setAmountManuallyEdited(false);
        }
    };

    const handleRecurrenceTypeChange = (val: typeof recurrenceType) => {
        const isMergeMode = !!mergeSources?.length;
        if (!isMergeMode || !amountManuallyEdited || val === 'custom') {
            applyRecurrenceTypeChange(val);
            return;
        }
        // User already typed their own amount - changing period would
        // silently overwrite it, so confirm first.
        setPendingRecurrenceType(val);
        setOpenModal('recalculateAmountWarning');
    };

    const confirmRecalculateAmount = () => {
        if (pendingRecurrenceType) {
            applyRecurrenceTypeChange(pendingRecurrenceType);
        }
        setOpenModal('');
        setPendingRecurrenceType(null);
    };

    React.useEffect(() => {
        const { data, isLoading, error } = fetchBanks;
        if (data && !isLoading) {
            const mappedBanks = data.map((item: any) => ({ value: String(item.id), label: `${item.bank} (${item.name})` }));
            // Add "Any bank account" option at the beginning
            const banksWithAny = [
                { value: '__any__', label: 'Any Bank Account' },
                ...mappedBanks
            ];
            setBankOptions(banksWithAny);
            
            // Auto-select bank if there's only one
            if (mappedBanks.length === 1) {
                setBank(mappedBanks[0].value);
                // Update initial state to reflect the auto-selected bank so it doesn't
                // trigger change detection — bail out without a new reference if this
                // effect re-runs (e.g. a bank refetch) and the value hasn't changed.
                setInitialState(prev => prev.bank === mappedBanks[0].value ? prev : { ...prev, bank: mappedBanks[0].value });
            }
        }
        if (error) {
            console.error('Error fetching banks:', error);
        }
    }, [fetchBanks]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchProvision;
        if (data && !isLoading) {
            setHasLinkedTransactions(data.hasLinkedTransactions || false);
            setHasShownLinkedTransactionsWarning(false);
            const appliedTaxes = data.appliedTaxBreakdown ? JSON.parse(data.appliedTaxBreakdown) : [];
            
            // Parse and store auto provision metadata
            if (data.autoProvisionMeta) {
                try {
                    const metaData = typeof data.autoProvisionMeta === 'string' ? JSON.parse(data.autoProvisionMeta) : data.autoProvisionMeta;
                    setAutoProvisionMeta(metaData);
                } catch (e) {
                    console.error('Error parsing autoProvisionMeta:', e);
                    setAutoProvisionMeta(null);
                }
            } else {
                setAutoProvisionMeta(null);
            }
            
            // Only load taxes if this is a fresh edit session (i.e., currently in edit mode)
            // This prevents resetting taxes when the effect re-runs
            if (isEdit?.state) {
                setAmount(data.amount);
                setDescription(data.description);
                setType(data.type === 'one-time' ? 'one-time' : 'recurring');
                setCategory(String(data.categoryId));
                setBank(data.bankAccountId ? String(data.bankAccountId) : '');
                setAppliedTaxes(appliedTaxes);
                setTaxManuallyEdited(true);

                if (data.type === 'one-time') {
                    setDate(data.transactionDate || '');
                    // Reset recurrence fields for one-time provisions
                    setRecurrenceType('monthly');
                    setRecurrencePattern('');
                    setOccurrences(1);
                    setStartDate('');
                    setEndDate('');
                    setEndDateType('on');
                } else {
                    const normalizedPattern = normalizeRecurrencePattern(data.recurrencePattern || '', data.startDate);
                    setRecurrenceType(data.type);
                    setRecurrencePattern(normalizedPattern);
                    setOccurrences(data.occurrenceCount || 1);
                    setStartDate(data.startDate || '');
                    setEndDate(data.endDate || '');
                    setEndDateType(data.endDateType || 'on');
                }

                // Save initial state for change tracking
                setInitialState({
                    amount: data.amount,
                    description: data.description,
                    type: data.type === 'one-time' ? 'one-time' : 'recurring',
                    date: data.type === 'one-time' ? (data.transactionDate || '') : '',
                    category: String(data.categoryId),
                    bank: data.bankAccountId ? String(data.bankAccountId) : '',
                    recurrenceType: data.type === 'one-time' ? 'daily' : data.type,
                    recurrencePattern: data.type === 'one-time' ? '' : normalizeRecurrencePattern(data.recurrencePattern || '', data.startDate),
                    occurrences: data.type === 'one-time' ? 1 : (data.occurrenceCount || 1),
                    startDate: data.type === 'one-time' ? '' : (data.startDate || ''),
                    endDate: data.type === 'one-time' ? '' : (data.endDate || ''),
                    endDateType: data.type === 'one-time' ? 'on' : (data.endDateType || 'on'),
                    appliedTaxes: appliedTaxes
                });
            }
        }
        if (error) {
            console.error('Error fetching provision:', error);
        }
    }, [fetchProvision, isEdit?.state]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteProvision;
        if (data && !isLoading) {
            showSuccess('Provision deleted successfully');
            setOpenModal('');
            if (onDelete && isEdit?.id) {
                onDelete(isEdit.id);
            }
            handleClose();
        }
        if (error) {
            showError(error ? error.toString() : 'Error deleting provision');
        }
    }, [deleteProvision]);

    const validate = () => {
        const newErrors: { 
            amount?: string, 
            description?: string,
            date?: string,
            category?: string,
            recurrenceType?: string,
            occurrences?: string,
            startDate?: string,
            endDate?: string,
            appliedTaxes?: string
        } = {};
        let requiredFields = [];
        if(type === 'one-time') {
            requiredFields = [
                'amount',
                'description',
                'date',
                'category'
            ];
        } else {
            requiredFields = [
                'amount',
                'description',
                'startDate',
                'endDate',
                'occurrences',
                'category'
            ];
            if (endDateType === 'never') {
                requiredFields = requiredFields.filter(field => field !== 'endDate' && field !== 'occurrences');
            } 
            if (occurrences <= 0) newErrors.occurrences = 'Occurrences must be greater than 0';
            
            // Validate recurrence pattern for non-daily and non-custom types
            if (recurrenceType !== 'daily' && recurrenceType !== 'custom' && !recurrencePattern) {
                newErrors.recurrenceType = 'Please select a recurrence pattern';
            }
        }

        const fieldValues: { [key: string]: any } = {
            amount,
            description,
            date,
            category,
            startDate,
            endDate,
            occurrences
        };

        requiredFields.forEach(field => {
            if (!fieldValues[field]) {
                newErrors[field as keyof typeof newErrors] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
            }
        });

        // A new provision can't be backdated before today - the calendar's
        // minDate already discourages picking one, but a prefilled date (e.g.
        // "Provision This" suggesting a date based on the detected pattern)
        // could still land in the past, so this is the check that actually
        // blocks submission. Skipped when editing an existing provision,
        // whose date may legitimately already be in the past.
        if (!isEdit?.state) {
            if (type === 'one-time' && date && date < todayStr && !newErrors.date) {
                newErrors.date = 'Date cannot be earlier than today';
            }
            if (type === 'recurring' && startDate && startDate < todayStr && !newErrors.startDate) {
                newErrors.startDate = 'Start date cannot be earlier than today';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const hasChanges = () => {
        return (
            amount !== initialState.amount ||
            description !== initialState.description ||
            type !== initialState.type ||
            date !== initialState.date ||
            category !== initialState.category ||
            bank !== initialState.bank ||
            recurrenceType !== initialState.recurrenceType ||
            recurrencePattern !== initialState.recurrencePattern ||
            occurrences !== initialState.occurrences ||
            startDate !== initialState.startDate ||
            endDate !== initialState.endDate ||
            endDateType !== initialState.endDateType ||
            JSON.stringify(appliedTaxes) !== JSON.stringify(initialState.appliedTaxes)
        );
    };

    const hasOccurrenceHistoryChoice = !!(isEdit?.state && type !== 'one-time' && occurrenceHistory && (occurrenceHistory.hasDeleted || occurrenceHistory.hasPushed));
    const canShowOccurrencesPanel = !!isEdit?.state && type === 'recurring';
    const occurrencesPanelVisible = canShowOccurrencesPanel && showOccurrencesPanel;
    const occurrenceRows = fetchOccurrenceTransactions.data?.data || [];

    const buildProvisionData = (): Provision => {
        // remove entries in appliedTaxes with undefined id
        const filteredTaxes = appliedTaxes.filter(tax => tax.id !== undefined);

        return type === 'one-time'
            ? {
                ...(isEdit?.state && isEdit?.id && { id: isEdit.id }),
                amount,
                description,
                type: 'one-time',
                transactionDate: date,
                bankAccountId: bank && bank !== '__any__' ? Number(bank) : undefined,
                categoryId: Number(category),
                appliedTaxBreakdown: filteredTaxes as { id: number; name: string }[],
                ...(sourceTransactionGuid ? { provisionThisSourceGuid: sourceTransactionGuid } : {})
            }
            : {
                ...(isEdit?.state && isEdit?.id && { id: isEdit.id }),
                amount,
                description,
                type: recurrenceType,
                occurrenceCount: occurrences,
                recurrencePattern,
                startDate,
                // Switching endDateType back to "never" (e.g. undoing a
                // prior Terminate) doesn't clear the endDate field/state
                // itself - without this, the stale date would still be
                // sent alongside endDateType: 'never'.
                endDate: endDateType === 'never' ? '' : endDate,
                endDateType,
                bankAccountId: bank && bank !== '__any__' ? Number(bank) : undefined,
                categoryId: Number(category),
                appliedTaxBreakdown: filteredTaxes as { id: number; name: string }[],
                ...(sourceTransactionGuid ? { provisionThisSourceGuid: sourceTransactionGuid } : {}),
                ...(hasOccurrenceHistoryChoice && { regenerateAll })
            };
    };

    const handleCreate = () => {
        setErrors({});
        if (!validate()) {
            showError('Please complete required fields');
            return;
        }
        // This template has deleted and/or pushed-forward occurrences -
        // confirm how to treat them before actually saving, rather than
        // silently applying whatever the radio last happened to be set to.
        if (hasOccurrenceHistoryChoice) {
            setOpenModal('occurrenceHistoryChoice');
            return;
        }
        onCreate(buildProvisionData());
    };

    const handleOccurrenceHistoryConfirm = () => {
        setOpenModal('');
        onCreate(buildProvisionData());
    };

    const handleClose = () => {
        setAmount('');
        setDescription('');
        setType('one-time');
        setDate('');
        setAppliedTaxes([]);
        setTaxManuallyEdited(false);
        setCategory('');
        setBank('');
        setRecurrenceType('daily');
        setRecurrencePattern('');
        setOccurrences(1);
        setStartDate('');
        setEndDate('');
        setEndDateType('never');
        setErrors({});
        setOpenModal('');
        setHasLinkedTransactions(false);
        setHasShownLinkedTransactionsWarning(false);
        setPendingFieldChange(null);
        setShowAddBankModal(false);
        setAutoProvisionMeta(null);
        setAmountManuallyEdited(false);
        setPendingRecurrenceType(null);
        setOccurrenceHistory(null);
        setRegenerateAll(false);
        setShowOccurrencesPanel(true);
        onClose();
    };

    const handleBankCreated = () => {
        // Fetch bank accounts after a new one is created
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user?.entityId }]);
        setCreateBanks({ url, method });
        setShowAddBankModal(false);
    };

    const showLinkedTransactionsWarning = (field: string, value: any) => {
        if (!hasLinkedTransactions || hasShownLinkedTransactionsWarning) {
            // If no linked transactions or warning already shown, just update the field
            if (field === 'amount') setAmount(value);
            if (field === 'description') setDescription(value);
            if (field === 'startDate') setStartDate(value);
            if (field === 'endDate') setEndDate(value);
            if (field === 'category') setCategory(value);
            if (field === 'bank') setBank(value);
            if (field === 'appliedTaxes') setAppliedTaxes(value);
            return;
        }
        // Show warning and store pending change
        setPendingFieldChange({ field, value });
        setOpenModal('linkedTransactionsWarning');
    };

    const handleLinkedTransactionsWarningConfirm = () => {
        if (pendingFieldChange) {
            const { field, value } = pendingFieldChange;
            if (field === 'amount') setAmount(value);
            if (field === 'description') setDescription(value);
            if (field === 'startDate') setStartDate(value);
            if (field === 'endDate') setEndDate(value);
            if (field === 'category') setCategory(value);
            if (field === 'bank') setBank(value);
            if (field === 'appliedTaxes') setAppliedTaxes(value);
        }
        setHasShownLinkedTransactionsWarning(true);
        setOpenModal('');
        setPendingFieldChange(null);
    };

    return (
        <>
        <AddBankAccount
            open={showAddBankModal}
            onClose={() => setShowAddBankModal(false)}
            onConfirm={(data) => {
                handleBankCreated();
            }}
        />
        <Dialog
            open={open}
            fullWidth={isMobile}
            maxWidth={isMobile ? false : false}
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                // The Dialog's own Paper is a transparent flex container -
                // the Occurrences panel and the Edit Provision form each
                // render as their own separate Paper card inside it (see
                // below), so they visually read as two adjacent modals
                // rather than one wide box. On desktop, the Occurrences card
                // is absolutely positioned inside an empty flex spacer that
                // stretches to match the form card's height (align-items:
                // 'stretch') - the spacer itself has no content, so it
                // contributes nothing to the row's own auto-height, meaning
                // the form card alone drives the pair's height, and
                // Occurrences just fills whatever height that turns out to
                // be (never the other way around).
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '90vh' }
                    : { background: 'transparent', boxShadow: 'none', display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 0.25, margin: 'auto', maxWidth: 'none', maxHeight: '90vh' }
            }}
        >
            <DeleteModal
                open={openModal === 'cancel'}
                message="Discard Changes"
                description="All changes made in this form will be lost if you proceed"
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    handleClose();
                }}
            />
            <DeleteModal
                open={openModal === 'linkedTransactionsWarning'}
                message="Apply Changes to Future Occurrences?"
                description="Some occurrences are already linked to real transactions. Changes to Amount, Description, Category, Tax or Bank Account will apply only to future unlinked occurrences. Linked and past occurrences will remain unchanged."
                confirmLabel="Continue"
                cancelLabel="Cancel"
                onCancel={() => {
                    setOpenModal('');
                    setPendingFieldChange(null);
                }}
                onConfirm={handleLinkedTransactionsWarningConfirm}
            />
            <DeleteModal
                open={!!pendingTaxToggle}
                message="Change Applied Taxes?"
                description="This will make the applied taxes different from this category's default. Continue?"
                confirmLabel="Continue"
                cancelLabel="Cancel"
                onCancel={() => setPendingTaxToggle(null)}
                onConfirm={confirmTaxToggle}
            />
            <DeleteModal
                open={openModal === 'recalculateAmountWarning'}
                message="Recalculate Amount?"
                description="Changing the period will recalculate the amount from the merged total and overwrite what you entered. Continue?"
                confirmLabel="Continue"
                cancelLabel="Cancel"
                onCancel={() => { setOpenModal(''); setPendingRecurrenceType(null); }}
                onConfirm={confirmRecalculateAmount}
            />
            <DeleteModal
                open={openModal === 'delete'}
                message="Delete Provision?"
                description="This action will permanently remove the selected provision."
                image={{ src: provisionImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    if (isEdit?.id) {
                        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.delete, [{ param: 'id', value: isEdit.id }]);
                        setDeleteProvision({ url, method });
                    }
                }}
            />
            <AutoProvisionMetaModal
                open={openModal === 'autoProvisionMeta'}
                onClose={() => setOpenModal('')}
                metadata={autoProvisionMeta}
            />
            <Dialog
                open={openModal === 'occurrenceHistoryChoice'}
                onClose={() => setOpenModal('')}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: isMobile ? 2 : 4, overflow: 'hidden' } }}
            >
                <DialogContent sx={{ p: isMobile ? 2.5 : 4, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={700} mb={1.5}>
                        Deleted or Pushed Occurrences
                    </Typography>
                    {occurrenceHistory && (
                        <Typography color="text.secondary" fontSize={15} mb={2}>
                            This provision has {[
                                occurrenceHistory.deletedCount > 0 ? `${occurrenceHistory.deletedCount} deleted` : null,
                                occurrenceHistory.pushedCount > 0 ? `${occurrenceHistory.pushedCount} pushed-forward` : null,
                            ].filter(Boolean).join(' and ')} occurrence{(occurrenceHistory.deletedCount + occurrenceHistory.pushedCount) !== 1 ? 's' : ''}.
                        </Typography>
                    )}
                    <RadioGroup
                        value={regenerateAll ? 'regenerate' : 'keep'}
                        onChange={(e) => setRegenerateAll(e.target.value === 'regenerate')}
                        sx={{ textAlign: 'left', mb: 1 }}
                    >
                        <FormControlLabel
                            value="keep"
                            sx={{ height: 32 }}
                            control={<Radio size="small" />}
                            label={<Typography fontSize={14}>Keep them as they are (recommended)</Typography>}
                        />
                        <FormControlLabel
                            value="regenerate"
                            sx={{ height: 32 }}
                            control={<Radio size="small" />}
                            label={<Typography fontSize={14}>Regenerate all occurrences, ignoring deleted/pushed history</Typography>}
                        />
                    </RadioGroup>
                    <Typography fontSize={12} color="text.secondary" mb={3}>
                        Occurrences already linked to a real transaction are never affected either way.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ borderRadius: 2, fontWeight: 600, fontSize: 16, py: 1.1, mb: 1.5, boxShadow: 'none', textTransform: 'none', color: '#fff' }}
                        onClick={handleOccurrenceHistoryConfirm}
                        disabled={isLoading}
                    >
                        Save Changes
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        sx={{ borderRadius: 2, fontWeight: 600, fontSize: 16, py: 1.1, textTransform: 'none' }}
                        onClick={() => setOpenModal('')}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                </DialogContent>
            </Dialog>
            <Paper sx={{
                borderRadius: isMobile ? '16px 16px 0 0' : (occurrencesPanelVisible ? '12px 0 0 12px' : 3),
                minWidth: isMobile ? undefined : 650,
                width: isMobile ? '100%' : undefined,
                p: isMobile ? 0.25 : 0.75,
                maxHeight: isMobile ? (occurrencesPanelVisible ? '55vh' : '90vh') : '90vh',
                overflowY: 'auto',
            }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: isMobile ? 16 : 22, pb: isMobile ? 0.5 : 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{isEdit?.state ? 'Edit Provision' : mergeSources?.length ? 'Merge Provisions' : 'Add New Provision'}</span>
                    {canShowOccurrencesPanel && (
                        <Button
                            size="small"
                            startIcon={<Eye size={16} />}
                            onClick={() => setShowOccurrencesPanel(v => !v)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: 13,
                                borderRadius: 2,
                                px: 1.25,
                                py: 0.25,
                                minWidth: 0,
                                color: showOccurrencesPanel ? 'var(--color-primary)' : 'var(--color-text-muted)',
                                background: showOccurrencesPanel ? 'rgba(var(--color-primary-rgb), 0.1255)' : 'var(--color-container-background)',
                                '&:hover': {
                                    background: showOccurrencesPanel ? 'rgba(var(--color-primary-rgb), 0.251)' : 'var(--color-container-background)',
                                },
                            }}
                        >
                            Occurrences
                        </Button>
                    )}
                </Box>
                {autoProvisionMeta && autoProvisionMeta.source === 'auto-provision' && (
                    <Chip
                        label="Auto Provision"
                        onClick={() => setOpenModal('autoProvisionMeta')}
                        sx={{
                            background: 'rgba(var(--color-primary-rgb), 0.1255)',
                            color: 'var(--color-primary)',
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: 'pointer',
                            '&:hover': {
                                background: 'rgba(var(--color-primary-rgb), 0.251)'
                            }
                        }}
                    />
                )}
            </DialogTitle>
            {!!mergeSources?.length && (
                <DialogContent sx={{ pt: 0, pb: isMobile ? 0.5 : 1, px: isMobile ? 1 : 2 }}>
                    <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: 14, mb: 1 }}>
                        From these:
                    </Typography>
                    <MaterialReactTable
                        columns={([
                            { accessorKey: 'description', header: 'Description', size: 180, Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{cell.getValue<string>()}</Typography> },
                            { accessorKey: 'type', header: 'Recurrence', size: 100, Cell: ({ cell }) => <Typography fontSize={12}>{cell.getValue<string>()?.toUpperCase()}</Typography> },
                            { accessorKey: 'categoryId', header: 'Category', size: 130, Cell: ({ cell }) => <Typography fontSize={12}>{mergeCategories?.find(c => String(c.value) === String(cell.getValue<number>()))?.label || '-'}</Typography> },
                            { accessorKey: 'startDate', header: 'Start Date', size: 90, Cell: ({ cell }) => <Typography fontSize={12}>{cell.getValue<string>() ? formatDate(new Date(cell.getValue<string>())) : '-'}</Typography> },
                            { accessorKey: 'endDate', header: 'End Date', size: 90, Cell: ({ cell }) => <Typography fontSize={12}>{cell.getValue<string>() ? formatDate(new Date(cell.getValue<string>())) : '-'}</Typography> },
                            { accessorKey: 'amount', header: 'Amount', size: 90, Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatAmount?.(Number(cell.getValue<string | number>()))}</Typography> },
                        ] as MRT_ColumnDef<Provision>[])}
                        data={mergeSources}
                        enablePagination={false}
                        enableTopToolbar={false}
                        enableBottomToolbar={false}
                        enableColumnActions={false}
                        enableSorting={false}
                        initialState={{ density: 'compact' }}
                        muiTableContainerProps={{ sx: { maxHeight: 200, overflowY: 'auto' } }}
                        muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                        muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                    />
                </DialogContent>
            )}
            <DialogContent sx={{ pt: isMobile ? 0.5 : 1, px: isMobile ? 1 : 2 }}>
                {!!mergeSources?.length && (
                    <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1.5 }}>To:</Typography>
                )}
                <Box display="flex" gap={isMobile ? 0.5 : 2} mb={isMobile ? 1 : 2} flexDirection={isMobile ? 'column' : 'row'}>
                    <Box flex={0.45} position="relative">
                        <Typography sx={{ fontWeight: 500, fontSize: isMobile ? 12 : 15, mb: isMobile ? 0.25 : 0.5, color: 'var(--color-text-muted)' }}>
                        Transaction Amount <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Amount"
                            value={amount}
                            onChange={e => { setAmountManuallyEdited(true); showLinkedTransactionsWarning('amount', e.target.value); }}
                            error={!!errors.amount}
                            helperText={errors.amount}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ position: 'absolute', bottom: -22, left: 0, width: isMobile ? 'auto' : 500 }}>
                            Enter a negative amount for expenses, or a positive amount for income.
                        </Typography>
                    </Box>
                    <Box flex={1} sx={isMobile ? { mt: 2 } : {}}>
                        <Typography sx={{ fontWeight: 500, fontSize: isMobile ? 12 : 15, mb: isMobile ? 0.25 : 0.5, color: 'var(--color-text-muted)' }}>
                        Description <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Description"
                            value={description}
                            onChange={e => showLinkedTransactionsWarning('description', e.target.value)}
                            error={!!errors.description}
                            helperText={errors.description}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                </Box>
                <Box>
                    <RadioGroup row value={type} onChange={e => setType(e.target.value as 'one-time' | 'recurring')}>
                        <FormControlLabel value="one-time" control={<Radio sx={{ color: 'var(--color-primary)' }} />} label="One Time" />
                        <FormControlLabel value="recurring" control={<Radio sx={{ color: 'var(--color-primary)' }} />} label="Recurring" />
                    </RadioGroup>
                </Box>
                <Divider sx={{ mb: 1.5 }}/>
                { type === 'one-time' ? (
                    <OneTimeProvision date={date} setDate={setDate} errorDate={!!errors.date} helperText={errors.date} minDate={isEdit?.state ? undefined : todayStr} />
                ) : (
                    <RecurringProvision
                        minDate={isEdit?.state ? undefined : todayStr}
                        recurrenceType={recurrenceType}
                        setRecurrenceType={handleRecurrenceTypeChange}
                        recurrencePattern={recurrencePattern}
                        setRecurrencePattern={setRecurrencePattern}
                        occurrences={occurrences}
                        setOccurrences={setOccurrences}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                        endDateType={endDateType}
                        setEndDateType={setEndDateType}
                        onStartDateChange={(val) => showLinkedTransactionsWarning('startDate', val)}
                        onEndDateChange={(val) => showLinkedTransactionsWarning('endDate', val)}
                        hasLinkedTransactions={hasLinkedTransactions}
                        errors={{
                            recurrenceType: errors.recurrenceType,
                            occurrences: errors.occurrences,
                            startDate: errors.startDate,
                            endDate: errors.endDate
                        }}
                    />
                )}
                <Divider sx={{ mb: 1.5 }}/>
                <Box mb={isMobile ? 1 : 2}>
                    <Typography sx={{ fontWeight: 500, fontSize: isMobile ? 12 : 15, mb: isMobile ? 0.25 : 0.5, color: 'var(--color-text-muted)' }}>
                        Category <span style={{ color: '#F44336' }}>*</span>
                    </Typography>
                    <TreeDropdown
                        options={categoryOptions}
                        value={category}
                        onChange={(selectedId) => showLinkedTransactionsWarning('category', selectedId || '')}
                        placeholder="Choose Category"
                        height={40}
                        width="100%"
                        error={!!errors.category}
                        helperText={errors.category}
                    />
                </Box>
                <Box mb={isMobile ? 1 : 2}>
                    <Typography fontSize={isMobile ? 12 : 15} color='var(--color-text-muted)' mb={0.5}>
                        Tax Applied
                    </Typography>
                    {taxDefinitions.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {taxDefinitions.map(tax => (
                                <FormControlLabel
                                    key={tax.id}
                                    control={
                                        <Checkbox
                                            checked={appliedTaxes.some(t => t.id === tax.id)}
                                            onChange={() => toggleTax(tax)}
                                            size="small"
                                            sx={{ color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                        />
                                    }
                                    label={<Typography fontSize={14}>{tax.name}</Typography>}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography sx={{
                            fontSize: 12,
                            fontStyle: 'italic',
                            textAlign: 'center',
                            border: '1px solid #514e0030',
                            borderRadius: 1,
                            py: 0.75,
                            background: '#fff70010'
                        }}>No tax definitions yet. <span style={{ textDecoration: 'underline', color: 'var(--color-primary)', cursor: 'pointer' }} onClick={() => navigate('/tax-definitions')}>Add here</span></Typography>
                    )}
                </Box>
                
                <Box mb={isMobile ? 1 : 2}>
                    <Typography sx={{ fontWeight: 500, fontSize: isMobile ? 12 : 15, mb: isMobile ? 0.25 : 0.5, color: 'var(--color-text-muted)' }}>
                        Bank Account
                    </Typography>
                    <Dropdown
                        options={bankOptions}
                        value={bank}
                        onChange={(val) => {
                            showLinkedTransactionsWarning('bank', val);
                        }}
                        placeholder="Choose Bank Account"
                        height={40}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: isMobile ? 'center' : 'space-between', px: isMobile ? 1 : 3, py: isMobile ? 0.75 : 2, gap: isMobile ? 0.5 : 0, flexDirection: isMobile ? 'column' : 'row' }}>
                <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, width: isMobile ? '100%' : 'auto', flexDirection: isMobile ? 'column' : 'row' }}>
                    <Button onClick={() => {
                        if (hasChanges()) {
                            setOpenModal('cancel');
                        } else {
                            handleClose();
                        }
                    }} variant="contained" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, fontSize: isMobile ? 12 : 14, py: isMobile ? 0.75 : 1, width: isMobile ? '100%' : 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                    Cancel
                    </Button>
                    {isEdit?.state && (
                        <Button onClick={() => setOpenModal('delete')} variant="contained" sx={{ background: '#F44336', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, fontSize: isMobile ? 12 : 14, py: isMobile ? 0.75 : 1, width: isMobile ? '100%' : 120, '&:hover': { background: '#d32f2f' } }}>
                        Delete
                        </Button>
                    )}
                </Box>
                
                <Button onClick={handleCreate} disabled={isLoading} variant="contained" sx={{ textWrap: 'nowrap', background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, fontSize: isMobile ? 12 : 14, py: isMobile ? 0.75 : 1, width: isMobile ? '100%' : 160, '&:hover': { background: 'var(--color-primary)' }, '&:disabled': { opacity: 0.6, background: 'var(--color-primary)' } }}>
                {isLoading ? 'Processing...' : isEdit?.state ? 'Save Changes' : 'Add Provision'}
                </Button>
            </DialogActions>
            </Paper>
            {occurrencesPanelVisible && (isMobile ? (
                <Paper sx={{
                    borderRadius: '0 0 16px 16px',
                    width: '100%',
                    flexShrink: 0,
                    maxHeight: '35vh',
                    display: 'flex',
                    // Clips at the card's own rounded-corner boundary - the
                    // scrollable region is ProvisionOccurrencesPanel's own
                    // inner row list, not this card.
                    overflow: 'hidden',
                }}>
                    <ProvisionOccurrencesPanel
                        occurrences={occurrenceRows}
                        isLoading={fetchOccurrenceTransactions.isLoading}
                        onClose={() => setShowOccurrencesPanel(false)}
                    />
                </Paper>
            ) : (
                // An empty positioning spacer, not the visible card itself -
                // it has no content of its own, so it contributes nothing to
                // the row's auto-height and the form card alone drives the
                // pair's height. align-items: 'stretch' on the row then
                // stretches this spacer to match that height, and the actual
                // card fills it via position: absolute + inset: 0.
                <Box sx={{ position: 'relative', width: 280, flexShrink: 0 }}>
                    <Paper sx={{
                        // Squared off on the edge touching the main modal, so the
                        // pair reads as one joined unit rather than two boxes
                        // with a rounded gap between them.
                        borderRadius: '0 12px 12px 0',
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        // Without this, ProvisionOccurrencesPanel's content -
                        // even though it's given height: 100% - spills out
                        // past this card's own bottom edge whenever it's
                        // taller than the main form card (its own inner list
                        // scrolls instead; nothing about this card should).
                        overflow: 'hidden',
                    }}>
                        <ProvisionOccurrencesPanel
                            occurrences={occurrenceRows}
                            isLoading={fetchOccurrenceTransactions.isLoading}
                            onClose={() => setShowOccurrencesPanel(false)}
                        />
                    </Paper>
                </Box>
            ))}
        </Dialog>
        </>
    );
};

export default AddProvisionPopup;
